// src/homepage/CennikSection.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Section = styled.section`
    padding: 96px 0; /* Reduced from 120px */
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #C0C0C0 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(153, 1, 71, 0.3) 50%,
                transparent 100%
        );
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 60px 60px;
`;

const Container = styled.div`
    max-width: 1200px; /* Reduced from 1400px */
    margin: 0 auto;
    padding: 0 2rem; /* Reduced from 3rem */
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const HeaderWrapper = styled.div`
    text-align: center;
    margin-bottom: 4rem; /* Reduced from 5rem */
`;

const HeaderTag = styled(motion.span)`
    display: inline-block;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.6rem 1.6rem; /* Reduced from 0.75rem 2rem */
    border-radius: 20px;
    font-size: 0.8rem; /* Reduced from 0.875rem */
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 1.2rem; /* Reduced from 1.5rem */
    box-shadow: 0 6px 20px rgba(153, 1, 71, 0.2); /* Reduced shadow */
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem; /* Reduced from 3.5rem */
    font-weight: 800;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.2rem; /* Reduced from 1.5rem */
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem; /* Reduced from 2.5rem */
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1rem; /* Reduced from 1.25rem */
    color: #555555;
    max-width: 500px; /* Reduced from 600px */
    margin: 0 auto;
    line-height: 1.5; /* Reduced from 1.6 */
    font-weight: 400;
`;

const PlansGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); /* Reduced from 450px */
    gap: 2.4rem; /* Reduced from 3rem */
    margin-bottom: 3.2rem; /* Reduced from 4rem */

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const PlanCard = styled(motion.article)`
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px; /* Reduced from 25px */
    padding: 2.4rem; /* Reduced from 3rem */
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08); /* Reduced shadow */
    border: 1px solid rgba(255, 255, 255, 0.8);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px; /* Reduced from 4px */
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    }

    &:hover {
        transform: translateY(-8px); /* Reduced from -10px */
        box-shadow: 0 24px 48px rgba(153, 1, 71, 0.15); /* Reduced shadow */
    }
`;

const PlanHeader = styled.div`
    margin-bottom: 2rem; /* Reduced from 2.5rem */
    text-align: center;
`;

const PlanName = styled.h3`
    font-size: 1.6rem; /* Reduced from 2rem */
    font-weight: 700;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.8rem; /* Reduced from 1rem */
`;

const PlanDescription = styled.p`
    color: #555555;
    font-size: 0.9rem; /* Reduced from 1.125rem */
    line-height: 1.5; /* Reduced from 1.6 */
`;

const PriceBadge = styled(motion.div)`
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.8rem 1.6rem; /* Reduced from 1rem 2rem */
    border-radius: 40px; /* Reduced from 50px */
    font-weight: 700;
    font-size: 1.2rem; /* Reduced from 1.5rem */
    display: inline-block;
    margin-bottom: 1.6rem; /* Reduced from 2rem */
    box-shadow: 0 8px 24px rgba(153, 1, 71, 0.3); /* Reduced shadow */
`;

const ServicesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 2rem 0; /* Reduced from 2.5rem */
    display: flex;
    flex-direction: column;
    gap: 1.2rem; /* Reduced from 1.5rem */
`;

const ServiceItem = styled(motion.li)`
    display: flex;
    align-items: flex-start;
    gap: 0.8rem; /* Reduced from 1rem */
    font-size: 0.9rem; /* Reduced from 1rem */
    color: #555555;
    line-height: 1.4; /* Reduced from 1.5 */
    padding: 0.8rem; /* Reduced from 1rem */
    background: rgba(248, 248, 248, 0.8);
    border-radius: 10px; /* Reduced from 12px */
    border-left: 2px solid #990147; /* Reduced from 3px */

    .check {
        flex-shrink: 0;
        margin-top: 0.1rem; /* Reduced from 0.125rem */
        color: #990147;
        width: 18px; /* Reduced from 20px */
        height: 18px; /* Reduced from 20px */
        filter: drop-shadow(0 1px 3px rgba(153, 1, 71, 0.2)); /* Reduced shadow */
    }
`;

const ServicePrice = styled.span`
    font-weight: 700;
    color: #990147;
    margin-left: auto;
    padding-left: 0.8rem; /* Reduced from 1rem */
    font-size: 0.95rem; /* Reduced from 1.1rem */
`;

const ServiceDescription = styled.p`
    font-size: 0.8rem; /* Reduced from 0.9rem */
    color: #777777;
    margin-top: 0.4rem; /* Reduced from 0.5rem */
    line-height: 1.3; /* Reduced from 1.4 */
    font-style: italic;
`;

const ImportantNote = styled(motion.div)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.95) 0%, rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 2.4rem; /* Reduced from 3rem */
    border-radius: 16px; /* Reduced from 20px */
    text-align: center;
    margin-top: 3.2rem; /* Reduced from 4rem */
    box-shadow: 0 16px 32px rgba(153, 1, 71, 0.3); /* Reduced shadow */
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        );
        pointer-events: none;
    }
`;

const NoteText = styled.p`
    color: #FFFFFF;
    font-size: 0.95rem; /* Reduced from 1.1rem */
    line-height: 1.5; /* Reduced from 1.7 */
    margin: 0;
    font-weight: 500;
    position: relative;
    z-index: 2;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); /* Reduced shadow */
`;

const CennikSection: React.FC = () => {


    return (
        <Section id="cennik">
            <BackgroundPattern />
            <Container>
                <HeaderWrapper>
                    <HeaderTag
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        Cenník
                    </HeaderTag>
                    <SectionTitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        Transparentné ceny
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        Jednoznačné ceny bez skrytých poplatkov. Všetky služby sú poskytované s maximálnou starostlivosťou.
                    </SectionSubtitle>
                </HeaderWrapper>

                <PlansGrid>
                    <PlanCard
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <PlanHeader>
                            <PlanName>Základné služby</PlanName>
                            <PlanDescription>
                                Komplexné psychiatrické vyšetrenia a konzultácie
                            </PlanDescription>
                        </PlanHeader>

                        <PriceBadge
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                        >
                            Od 10€
                        </PriceBadge>

                        <ServicesList>
                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Prvé vstupné psychiatrické vyšetrenie
                                    <ServiceDescription>
                                        Anamnéza, diagnostika, liečebný plán, edukácia
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>250€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Jednorazové psychiatrické vyšetrenie
                                    <ServiceDescription>
                                        Pre sociálne, posudkové alebo úradné účely
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>250€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Kontrolné vyšetrenie - rozšírené
                                    <ServiceDescription>
                                        Do 45 minút vrátane psychoterapie (možné online)
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>80€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Kontrolné vyšetrenie - základné
                                    <ServiceDescription>
                                        Do 10 minút, aktualizácia liečby (možné online)
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>30€</ServicePrice>
                            </ServiceItem>
                        </ServicesList>

                    </PlanCard>

                    <PlanCard
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        <PlanHeader>
                            <PlanName>Špeciálne služby</PlanName>
                            <PlanDescription>
                                Doplnkové a nadštandardné služby podľa vašich potrieb
                            </PlanDescription>
                        </PlanHeader>

                        <PriceBadge
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            viewport={{ once: true }}
                        >
                            Od 10€
                        </PriceBadge>

                        <ServicesList>
                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Vypísanie receptov a základná kontrola
                                    <ServiceDescription>
                                        V rámci zastupovania
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>10€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Podrobná lekárska správa
                                    <ServiceDescription>
                                        Pre Sociálnu poisťovňu, úradné účely, cestovanie
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>24.80€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Konzultácia s príbuznými
                                </div>
                                <ServicePrice>40€</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Ročný paušálny poplatok
                                    <ServiceDescription>
                                        Recepty na požiadanie, mailová a SMS komunikácia
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>30€/rok</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Vyšetrenie mimo ordinačných hodín
                                    <ServiceDescription>
                                        Vo večerných hodinách, víkendy, v domácom prostredí
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>+100%</ServicePrice>
                            </ServiceItem>

                            <ServiceItem
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.7 }}
                                viewport={{ once: true }}
                            >
                                <svg className="check" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    Kopírovanie
                                    <ServiceDescription>
                                        Jedna strana A4
                                    </ServiceDescription>
                                </div>
                                <ServicePrice>0.40€</ServicePrice>
                            </ServiceItem>
                        </ServicesList>

                    </PlanCard>
                </PlansGrid>

                <ImportantNote
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                >
                    <NoteText>
                        💡 Dôležité: Ak sa nemôžete dostaviť na vyšetrenie, je nutné sa z termínu odhlásiť a termín uvoľniť pre iného pacienta.
                        Odhlásenie z termínu minimálne 24 hodín vopred je bezplatné. Neskoré odhlásenie alebo nepríchod je spoplatnený storno poplatkom 40€.
                    </NoteText>
                </ImportantNote>
            </Container>
        </Section>
    );
};

export default CennikSection;